/**
 * summaryFreshness.js — s111 (TestFlight build 78 feedback, 2026-10-18).
 *
 * ★★★ WHY THIS EXISTS. The server re-ranked the shared differential from the patient's audiogram
 *   (records_refresh, D514: patient_summary AND pre_visit_summary, otosclerosis #1 at 23:39Z). The MD
 *   panel showed the new list; the patient's Case Summary still showed the old one (vestibular
 *   schwannoma #1) four minutes later. The server copy was right. loadCases() builds the open case as
 *   `{ ...api, ...local }` — the LOCAL copy wins every key it is not told about — and patientSummary
 *   was never told, so the summary the app held from before the refresh outlived every reload.
 *
 * ★★ THE RULE: the server's summary wins when it is at least as new as the local one. "New" is the
 *   stamp the server writes: refreshedWithRecords.at (a records refresh) else generatedAt. A local
 *   copy only wins when it is strictly newer (the patient just regenerated in this session and the
 *   list response is older), or when the server sent none. `detectPrep` is kept from the local copy
 *   when the server's has none (v220.9.3: Detect writes it client-side).
 *
 * ★ hasUnseenSummaryUpdate / markSummaryUpdateSeen: the Describe step's "Updated" pill (Dr. Rami,
 *   2026-10-19: "notify and indicate on describe step"; pill only, no banner). Per device, keyed by
 *   case id → the refreshedWithRecords.at the patient last opened. Storage can throw (private mode):
 *   every access is guarded, and a failure means "no pill", never a crash.
 *
 * Pure apart from the guarded storage helpers. scripts/check-summary-freshness.mjs loads this file.
 */

const obj = (v) => {
  if (typeof v === 'string') {
    try { v = JSON.parse(v) } catch { return null }
  }
  return v && typeof v === 'object' && !Array.isArray(v) ? v : null
}

/** The server-written stamp of a summary, in ms (0 when none). */
export function summaryStamp(summary) {
  const s = obj(summary)
  if (!s) return 0
  const at = (s.refreshedWithRecords && s.refreshedWithRecords.at) || s.generatedAt || ''
  if (!at) return 0
  // generatedAt is written without a zone (Python isoformat of a UTC time): read it as UTC.
  const iso = /[zZ]|[+-]\d\d:?\d\d$/.test(at) ? at : `${at}Z`
  const ms = Date.parse(iso)
  return Number.isNaN(ms) ? 0 : ms
}

/** Which summary the open case keeps: the server's unless the local one is strictly newer. */
export function newerSummary(serverSummary, localSummary) {
  const server = obj(serverSummary)
  const local = obj(localSummary)
  if (!server) return localSummary || null
  if (!local) return serverSummary
  if (summaryStamp(local) > summaryStamp(server)) return localSummary
  if (local.detectPrep && !server.detectPrep) return { ...server, detectPrep: local.detectPrep }
  return serverSummary
}

const SEEN_KEY = 'orella_summary_update_seen_v1'

function readSeen() {
  try {
    const raw = window.localStorage.getItem(SEEN_KEY)
    const v = raw ? JSON.parse(raw) : {}
    return v && typeof v === 'object' ? v : {}
  } catch { return {} }
}

/** The records-refresh stamp of a case's summary ('' when it was never refreshed). */
export function refreshStampOf(c) {
  const s = obj(c && (c.patientSummary || c.patient_summary))
  return (s && s.refreshedWithRecords && typeof s.refreshedWithRecords.at === 'string') ? s.refreshedWithRecords.at : ''
}

/** True when the case's list was refreshed with records after the patient last opened it here. */
export function hasUnseenSummaryUpdate(c, seen = readSeen()) {
  const at = refreshStampOf(c)
  if (!at || !c || !c.id) return false
  return seen[c.id] !== at
}

/** The patient opened the refreshed list: the pill goes. */
export function markSummaryUpdateSeen(c) {
  const at = refreshStampOf(c)
  if (!at || !c || !c.id) return
  try {
    const seen = readSeen()
    if (seen[c.id] === at) return
    seen[c.id] = at
    window.localStorage.setItem(SEEN_KEY, JSON.stringify(seen))
  } catch { /* storage unavailable: the pill simply stays */ }
}
